import { useEffect, useState } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  ToasterContent,
} from '@repo/videa-components/src/index';
import { Button } from '@repo/videa-components/src/ui/button';
import { Input } from '@repo/videa-components/src/ui/input';
import { useAtomValue, useSetAtom } from 'jotai';
import { toast } from 'sonner';

import {
  useCreatePracticeGroup,
  useGetPracticeGroups,
  useUpdatePracticeGroup,
} from '../../../react-query/insights/usePracticeGroups';

import { isOnUpdatePageAtom, selectedPracticeGroupAtom } from './atoms';
import { PracticeTable } from './PracticeTable';

export const UpdatePracticeGroups = () => {
  const setIsOnUpdatePage = useSetAtom(isOnUpdatePageAtom);
  const selectedPracticeGroup = useAtomValue(selectedPracticeGroupAtom);
  const shouldCreateNew = !selectedPracticeGroup;
  const [selectedPractices, setSelectedPractices] = useState<string[]>([]);

  const { data: practiceGroups } = useGetPracticeGroups();
  const practiceGroup = practiceGroups?.data?.find(
    (group) => group.id === selectedPracticeGroup,
  );

  const handleSuccess = () => {
    setIsOnUpdatePage(false);
    toast(
      <ToasterContent
        toast={toast}
        title="Region group created!"
        description="Your new region group is saved. Updated region groups will sync to Insights overnight and be available tomorrow."
        buttonVariant="success"
      />,
    );
  };

  const { mutate: updatePracticeGroup } = useUpdatePracticeGroup(handleSuccess);
  const { mutate: createPracticeGroup } = useCreatePracticeGroup(handleSuccess);

  const [regionName, setRegionName] = useState<string>(
    practiceGroup?.name ?? '',
  );

  useEffect(() => {
    if (practiceGroup) {
      setSelectedPractices(
        practiceGroup.practices.map((practice) => practice.practiceVideaId),
      );
    }
  }, [practiceGroup]);

  return (
    <div className="vai-flex vai-flex-col vai-gap-6">
      <div className="vai-flex vai-flex-row vai-items-center vai-justify-between">
        <span className="vai-typography-h2 vai-text-zinc-800">
          {shouldCreateNew ? 'Create New' : 'Edit'} Region
        </span>
        <div className="vai-flex vai-flex-row vai-gap-2">
          <AlertDialog>
            <AlertDialogTrigger>
              <Button
                variant="outline"
                size="sm"
                className="vai-w-20"
                dataTestId="cancel-practice-group-button"
              >
                Cancel
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogDescription>
                You have unsaved changes. If you leave now, your work will be
                lost.
              </AlertDialogDescription>
              <AlertDialogFooter>
                <AlertDialogCancel dataTestId="keep-editing-practice-group-button-alert-dialog">
                  Keep Editing
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => {
                    setIsOnUpdatePage(false);
                  }}
                  dataTestId="continue-practice-group-button-alert-dialog"
                >
                  Continue
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            variant="default"
            size="sm"
            className="vai-w-20"
            dataTestId="save-practice-group-button"
            disabled={regionName.length === 0 || selectedPractices.length === 0}
            onClick={() => {
              if (shouldCreateNew) {
                createPracticeGroup({
                  name: regionName,
                  practices: selectedPractices,
                });
              } else {
                updatePracticeGroup({
                  id: selectedPracticeGroup,
                  name: regionName,
                  practices: selectedPractices,
                });
              }
            }}
          >
            Save
          </Button>
        </div>
      </div>
      <div className="vai-flex vai-flex-col vai-gap-2 vai-w-1/2">
        <span className="vai-typography-large vai-text-zinc-800">
          Region Name
        </span>
        <Input
          value={regionName}
          placeholder="(e.g. West Coast Region)"
          onChange={(e) => {
            setRegionName(e.target.value);
          }}
          className="vai-m-0"
          dataTestId="region-name-input"
        />
        <span className="vai-typography-muted">
          The group name will be what you see as a selectable filter in Insights
        </span>
      </div>
      <PracticeTable
        selectedPractices={selectedPractices}
        setSelectedPractices={setSelectedPractices}
      />
    </div>
  );
};
